import { Box, Grid, Button, Typography, IconButton } from "@material-ui/core";
import { Form, Modal } from "@components";
import React, { useCallback, useContext } from "react";
import { useHistory } from "react-router";
import { UPDATE_FILTERS_DATA } from "../state";
import { TableContext } from "../table";
import { Search } from "./search-filters";
import { Actions } from "../../../screens/request/components/actions";
import CloseIcon from "@material-ui/icons/Close";
import { Api } from "@api";
import Hooks from "@hooks";
import { alertService } from "@services";

import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import {
  createStyles,
  Theme,
  withStyles,
  WithStyles,
} from "@material-ui/core/styles";
import { messages } from "../../../screens/request/steps/user-information/views/users-list/add-user-modal/messages";

const styles = (theme: Theme) =>
  createStyles({
    root: {
      margin: 0,
      padding: theme.spacing(2),
    },
    closeButton: {
      position: "absolute",
      right: theme.spacing(1),
      top: theme.spacing(1),
      color: theme.palette.grey[500],
    },
  });

export interface DialogTitleProps extends WithStyles<typeof styles> {
  id: string;
  children: React.ReactNode;
  onClose: () => void;
}

interface Props {
  onClickAdd: () => void;
}

type FormValues = {
  id: number;
  vipEmail: string;
  approverEmail: string;
  void: boolean;
  active: boolean;
  has_started: "yes" | "no";
};

type Data = {
  has_started: "yes" | "no";
};

type Key = keyof FormValues;

export const VIPToolbar: React.FC<Props> = ({ onClickAdd }) => {
  const history = useHistory();
  const { dispatch } = useContext(TableContext);
  const [isOpenConfirmationModal, setIsOpenConfirmationModal] = React.useState<
    boolean
  >(false);
  const [pending, setPending] = React.useState(false);
  const onSearchUpdate = useCallback(
    (term: string) => {
      dispatch({
        type: UPDATE_FILTERS_DATA,
        filtersData: { searchTerm: term },
      });
    },
    [dispatch]
  );

  const form = Form.useManager<FormValues>({
    id: 0,
    vipEmail: "",
    approverEmail: "",
    void: false,
    active: false,
    has_started: "yes",
  });

  const [
    directory_vipEmails,
    ,
    directory_vipEmails_fetching,
  ] = Hooks.useDirectory(form.values.vipEmail);
  const [
    directory_approverEmails,
    ,
    directory_approverEmails_fetching,
  ] = Hooks.useDirectory(form.values.approverEmail);
  const [vipEmail, vipEmail_error, vipEmail_fetching] = Hooks.useUserID(
    form.values.vipEmail
  );
  const [
    approverEmail,
    approverEmail_error,
    approverEmail_fetching,
  ] = Hooks.useUserID(form.values.approverEmail);
  const [message, setMessage] = React.useState<{
    text: string;
    error?: boolean;
  }>({
    text: messages.has_started,
  });
  const prev = {
    form_errors: Hooks.usePrevious(form.errors),
    form_values: Hooks.usePrevious(form.values),
    vipEmail_fetching: Hooks.usePrevious(vipEmail_fetching),
    approverEmail_fetching: Hooks.usePrevious(approverEmail_fetching),
  };

  function handleVIPEmailValueInput(name: string, value: string): void {
    form.set(Form.target.VALUE, ["vipEmail", value]);
  }

  function handleApproverEmailValueInput(name: string, value: string): void {
    form.set(Form.target.VALUE, ["approverEmail", value]);
  }

  const handleVIPEmailValueChange = React.useCallback(
    (name: string, value: string) => {
      form.set(Form.target.VALUES, {
        vipEmail: value,
      });
      form.set(Form.target.ERRORS, {
        vipEmail: undefined,
      });
      validate([name, value]);
      if (
        prev.form_values?.vipEmail !== form.values.vipEmail &&
        !!vipEmail_error
      ) {
        setMessage({ text: messages.user_id_not_found, error: true });
      }
    },
    [form, prev.form_values, vipEmail_error, validate]
  );

  const handleApproverEmailValueChange = React.useCallback(
    (name: string, value: string) => {
      form.set(Form.target.VALUES, {
        approverEmail: value,
      });
      form.set(Form.target.ERRORS, {
        approverEmail: undefined,
      });
      validate([name, value]);
      if (
        prev.form_values?.approverEmail !== form.values.approverEmail &&
        !!approverEmail_error
      ) {
        setMessage({ text: messages.user_id_not_found, error: true });
      }
    },
    [form, prev.form_values, approverEmail_error, validate]
  );

  function getVIPDirectoryOptions(): string[] {
    return directory_vipEmails
      ? directory_vipEmails.map((user) => user.email)
      : [];
  }

  function getApproverDirectoryOptions(): string[] {
    return directory_approverEmails
      ? directory_approverEmails.map((user) => user.email)
      : [];
  }

  function validate([name, value]: [string, any]): Promise<
    [keyof Data, Error?]
  > {
    let error: Error | undefined;

    switch (name) {
      case "vipEmail":
      case "approverEmail":
        error =
          Form.Validators.hasValue(value) || Form.Validators.isEmail(value);
        break;
    }

    return Promise.resolve([name as keyof Data, error]);
  }

  function handleAddButtonClick(): void {
    setPending(true);
    Api.vip
      .postVIP({
        ...form.values,
      })
      .then(() => {
        setPending(false);
        setIsOpenConfirmationModal(false);
        form.reset();
        handleClose();
        onClickAdd();
      })
      .catch((error) => {
        setPending(false);
        // [TODO] Handle error
        alertService.danger({
          statusCode: error.response.status,
          msg: "Please check if VIP Email already exists.",
        });

        setIsOpenConfirmationModal(false);
      });
  }

  function handleSubmitButtonClick(): void {
    Promise.all(Object.entries(form.values).map(validate)).then(
      (validations) => {
        const valid = validations.reduce((valid, [name, error]) => {
          return valid && !error;
        }, true);
        if (valid) {
          setIsOpenConfirmationModal(true);
        } else {
          validations.forEach(([name, error]) => {
            form.set(Form.target.ERROR, [name, error]);
          });
        }
      }
    );
  }

  function handleChangeIsActive(name: Key, value: any): void {
    form.set(Form.target.VALUE, [name, value]);
  }

  const [open, setOpen] = React.useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    form.reset();
    setOpen(false);
  };
  const styles = (theme: Theme) =>
    createStyles({
      root: {
        margin: 0,
        padding: theme.spacing(2),
      },
      closeButton: {
        position: "absolute",
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500],
      },
    });

  const DialogTitle = withStyles(styles)((props: DialogTitleProps) => {
    const { children, classes, onClose, ...other } = props;
    return (
      <MuiDialogTitle disableTypography className={classes.root} {...other}>
        <Typography variant="h6">{children}</Typography>
        {onClose ? (
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={onClose}
          >
            <CloseIcon />
          </IconButton>
        ) : null}
      </MuiDialogTitle>
    );
  });

  return (
    <Box mt={2} mb={2}>
      <Grid container direction="row" spacing={1} justify="space-between">
        <Grid item xs={"auto"}>
          <Actions>
            <Actions.Action onClick={handleClickOpen}>
              + Add New VIP Approver
            </Actions.Action>
          </Actions>
        </Grid>
        <Grid item xs={3}>
          <Search
            onSearchUpdate={onSearchUpdate}
            placeholder={"Search by Name or Email"}
          ></Search>
        </Grid>
      </Grid>
      <Modal
        isOpen={isOpenConfirmationModal}
        positionButton="center"
        actions={[
          {
            label: "Cancel",
            variant: "outlined",
            handler: () => {
              setIsOpenConfirmationModal(false);
            },
          },
          {
            label: pending ? "Submitting..." : "Submit",
            handler: () => {
              handleAddButtonClick();
            },
            disabled: pending,
          },
        ]}
      >
        <Typography align="center" paragraph>
          Are you sure you want to add this VIP Approver?
        </Typography>
      </Modal>

      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="form-dialog-title"
        fullWidth={true}
        maxWidth={"sm"}
      >
        <DialogTitle id="form-dialog-title" onClose={handleClose}>
          Add New VIP Approver
        </DialogTitle>
        <DialogContent>
          <Form>
            <Form.Autocomplete
              error={form.errors.vipEmail}
              label="VIP Email"
              loading={directory_vipEmails_fetching}
              name="vipEmail"
              onChange={handleVIPEmailValueChange}
              onInput={handleVIPEmailValueInput}
              options={getVIPDirectoryOptions()}
              size={12}
              value={form.values.vipEmail}
              required={true}
            />
            <Form.Autocomplete
              error={form.errors.approverEmail}
              label="Approver Email"
              loading={directory_approverEmails_fetching}
              name="approverEmail"
              onChange={handleApproverEmailValueChange}
              onInput={handleApproverEmailValueInput}
              options={getApproverDirectoryOptions()}
              size={12}
              value={form.values.approverEmail}
              required={true}
            />
            <Form.Checkbox
              label="Active"
              name="active"
              onChange={handleChangeIsActive}
              size={6}
              value={form.values.active}
            />
          </Form>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleSubmitButtonClick} color="primary">
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
